import { Checkbox, Text } from "@/components/DesignSystem";
import DetailSection from "@/components/shared/DetailsSections";
import { QueryKeys } from "@/constants/QueryKeys";
import { useCanWrite } from "@/hooks/useCanWrite";
import { useSnackbar } from "@/providers/GlobalSnackbarProvider";
import { UserApi } from "@/utilities/api/UserApi";
import { ApiUser } from "@api/interfaces";
import { Grid, Stack } from "@mui/material";
import { UserPermission, hasUserPermission } from "@shared/enums";
import { useMutation, useQueryClient } from "react-query";

const AdminDetails = ({ admin }: { admin: ApiUser }) => {
  const canWrite = useCanWrite();
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();

  const isReadOnly = !hasUserPermission(admin.permissions, UserPermission.InternalCanWrite);

  const { mutate: setReadOnly, isLoading } = useMutation(
    (readOnly: boolean) => UserApi.update(admin.id, {
      permissions: { [UserPermission.InternalCanWrite]: !readOnly },
    }),
    {
      onError: (error: { message: string }) => {
        openSnackbar(error.message || 'An error has occurred');
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries([QueryKeys.LIST_USERS]);
      },
    },
  );

  return (
    <Grid
      alignItems="start"
      container
      direction="row"
      spacing={4}
    >
      <Grid item md={4}>
        <Stack>
          <DetailSection
            details={`${admin.firstName} ${admin.lastName}`}
            label="Name"
            testID="admin-name"
          />
          <DetailSection
            details={admin.email}
            label="Email"
            testID="admin-email"
          />
          <DetailSection
            details={admin.telephone}
            label="Phone"
            testID="admin-phone"
          />
        </Stack>
      </Grid>
      <Grid item md={4}>
        <Stack>
          <DetailSection
            details={admin.address1}
            label="Address1"
            testID="admin-address1"
          />
          <DetailSection
            details={admin.address2}
            label="Address2"
            testID="admin-address2"
          />
          <DetailSection
            details={admin.city}
            label="City"
            testID="admin-city"
          />
        </Stack>
      </Grid>
      <Grid item md={4}>
        <Stack>
          <DetailSection
            details={admin.state}
            label="State"
            testID="admin-state"
          />
          <DetailSection
            details={admin.postal}
            label="Zip"
            testID="admin-zip"
          />
          <DetailSection
            details={admin.updatedAt.toLocaleDateString()}
            label="Updated"
            testID="admin-updated"
          />
          <Checkbox
            checked={isReadOnly}
            disabled={!canWrite || isLoading}
            onChangeChecked={setReadOnly}
            testID="admin-read-only"
          >
            <Text category="body-large">Read-only</Text>
          </Checkbox>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default AdminDetails;
